'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { createOnrampClient } from '@morfi/onramp-sdk';
import { usePathname, useSearchParams } from 'next/navigation';
import { type ReactNode, useEffect, useState } from 'react';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { toSdkApiBase } from '@/lib/network/sdk-api-base';
import { useAppRouter } from '@/lib/network/use-app-router';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';
import { FundingHistory } from './history/funding-history';
import { useFundingRealtime } from './shared/funding-realtime';
import { fundingTelemetry } from './shared/funding-telemetry';
import { fundingReceiptId } from './shared/receipt-link';
import { useFundingResource } from './shared/use-funding-resource';

/** Mounted only inside the authenticated own-portfolio branch, independently of payment rollout. */
export function FundingReceiptLink(t0) {
  const $ = _c(22);
  const {
    userId
  } = t0;
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useAppRouter();
  const config = useNetworkConfig();
  let t1;
  if ($[0] !== params) {
    t1 = fundingReceiptId(params.getAll("onrampOperation"));
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const operationId = t1;
  let t2;
  if ($[2] !== params) {
    t2 = params.getAll("onrampHistory");
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const historyValues = t2;
  const historyRequested = historyValues.length === 1 && historyValues[0] === "1";
  let t3;
  if ($[4] !== historyRequested || $[5] !== operationId || $[6] !== params) {
    t3 = params.has("onrampOperation") && operationId === null || params.has("onrampHistory") && !historyRequested || historyRequested && params.has("onrampOperation");
    $[4] = historyRequested;
    $[5] = operationId;
    $[6] = params;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const invalid = t3;
  let t4;
  let t5;
  if ($[8] !== invalid) {
    t4 = () => {
      if (invalid) {
        logger.warn("onramp.receipt_link_rejected", {
          reason: "invalid_identifier"
        });
      }
    };
    t5 = [invalid];
    $[8] = invalid;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== params || $[12] !== pathname || $[13] !== router) {
    t6 = function dismiss() {
      const next = new URLSearchParams(params);
      next.delete("onrampHistory");
      next.delete("onrampOperation");
      const query = next.toString();
      router.replace(`${pathname}${query ? `?${query}` : ""}`, {
        scroll: false
      });
    };
    $[11] = params;
    $[12] = pathname;
    $[13] = router;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const dismiss = t6;
  let t7;
  if ($[15] !== config || $[16] !== dismiss || $[17] !== historyRequested || $[18] !== invalid || $[19] !== operationId || $[20] !== userId) {
    t7 = !invalid && (operationId || historyRequested) ? <ReceiptClient key={`${userId}:${config.apiUrl}:${operationId}`} apiUrl={config.apiUrl} {...operationId ? {
      operationId
    } : {}} onDismiss={dismiss} /> : null;
    $[15] = config;
    $[16] = dismiss;
    $[17] = historyRequested;
    $[18] = invalid;
    $[19] = operationId;
    $[20] = userId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function ReceiptClient(t0) {
  const $ = _c(9);
  const {
    apiUrl,
    operationId,
    onDismiss
  } = t0;
  const [open, setOpen] = useState(true);
  const {
    openLogin
  } = useAuthGate();
  let t1;
  if ($[0] !== apiUrl) {
    t1 = () => createOnrampClient({
      telemetry: fundingTelemetry,
      baseUrl: process.env.NODE_ENV === "development" ? window.location.origin : toSdkApiBase(apiUrl),
      getAuthHeaders: _temp
    });
    $[0] = apiUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const client = useFundingResource(t1);
  const realtime = useFundingRealtime(client, open);
  let t2;
  if ($[2] !== client || $[3] !== onDismiss || $[4] !== open || $[5] !== openLogin || $[6] !== operationId || $[7] !== realtime) {
    t2 = client ? <FundingHistory client={client} {...realtime ? {
      realtime
    } : {}} open={open} {...operationId ? {
      initialOperationId: operationId
    } : {}} onSignIn={openLogin} onOpenChange={next => {
      setOpen(next);
      if (!next) {
        onDismiss();
      }
    }} /> : null;
    $[2] = client;
    $[3] = onDismiss;
    $[4] = open;
    $[5] = openLogin;
    $[6] = operationId;
    $[7] = realtime;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
async function _temp() {
  return {};
}
export function FundingHistoryEntry(t0) {
  const $ = _c(4);
  const {
    onClose
  } = t0;
  const {
    user,
    isAuthenticated
  } = useAuth();
  const config = useNetworkConfig();
  if (!isAuthenticated || !user) {
    return null;
  }
  const t1 = `${user.id}:${config.apiUrl}`;
  let t2;
  if ($[0] !== config.apiUrl || $[1] !== onClose || $[2] !== t1) {
    t2 = <ReceiptClient key={t1} apiUrl={config.apiUrl} onDismiss={onClose} />;
    $[0] = config.apiUrl;
    $[1] = onClose;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}